interface TableData {
  headers: string[];
  rows: (string | number)[][];
}

interface TabData {
  table1: TableData;
  table2: TableData;
  table3: TableData;
}

interface SessionOutput {
  tabs: string[];
  content: {
    [tabName: string]: Array<{
      type: string;
      content: string;
      style?: { grid: number };
    }>;
  };
}

export class DataTransformer {
  static extractVendorTotalsFromSummary(sessionData: any): Record<string, number> {
    const output: SessionOutput = sessionData?.output;
    if (!output || !output.content) throw new Error('Missing output content');
    
    // Prefer legacy 'Vendor to Vendor', else support new 'Cost Benchmarking',
    // else fallback to the first available tab in output.tabs
    const preferredTabName = output.tabs?.includes('Vendor to Vendor')
      ? 'Vendor to Vendor'
      : output.tabs?.includes('Cost Benchmarking')
        ? 'Cost Benchmarking'
        : (output.tabs && output.tabs.length > 0 ? output.tabs[0] : undefined);

    if (!preferredTabName || !output.content[preferredTabName]) {
      throw new Error('No suitable tab found in output to extract vendor totals');
    }

    const tabArr = output.content[preferredTabName];
    const markdown = tabArr.find((item: any) => item.type === 'markdown')?.content;
    if (!markdown) throw new Error('Markdown content missing in selected tab');

    const totals: Record<string, number> = {};

    // First, try to detect if this is a single vendor scenario
    const singleVendorMatch = markdown.match(/Total Quote Value:\s*₹([\d,]+)/);
    if (singleVendorMatch) {
      // This is a single vendor scenario
      let vendorName = 'ABG'; // default
      
      // Try to extract vendor name from context
      const vendorNameMatches = [
        markdown.match(/vendor quote \(([^)]+)\)/i),
        markdown.match(/Analysis of ([^Benchmark\s]+)(?:\s+Benchmark)?\s+Quote/i),
        markdown.match(/Event:\s*Conference at ([^,]+)/i),
        markdown.match(/Venue:\s*([^,\n]+)/i)
      ];
      
      for (const match of vendorNameMatches) {
        if (match && match[1]) {
          const extracted = match[1].trim();
          if (extracted && !extracted.toLowerCase().includes('benchmark')) {
            vendorName = extracted;
            break;
          }
        }
      }
      
      const totalValue = parseInt(singleVendorMatch[1].replace(/,/g, ''));
      if (!isNaN(totalValue)) {
        totals[vendorName] = totalValue;
        return totals;
      }
    }
    
    // Multi-vendor scenario - look for vendor-specific totals
    const multiVendorRegex = /(?:Vendor\s+[A-Z]\s*\([^)]+\)|[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*):\s*₹([\d,]+)/g;
    let match;
    while ((match = multiVendorRegex.exec(markdown)) !== null) {
      const vendorText = match[0];
      const value = parseInt(match[1].replace(/,/g, ''));
      
      // Extract clean vendor name
      let vendor = vendorText.split(':')[0].trim();
      const vendorInParens = vendor.match(/Vendor\s+[A-Z]\s*\(([^)]+)\)/);
      if (vendorInParens) {
        vendor = vendorInParens[1].trim();
      }
      
      if (!isNaN(value) && vendor) {
        totals[vendor] = value;
      }
    }
    
    return totals;
  }

  static extractAllTablesFromMarkdown(markdown: string): Array<{ heading: string; table: TableData }> {
    const results: Array<{ heading: string; table: TableData }> = [];
    let tableCount = 0;
    
    // Extract markdown tables - improved regex to catch more formats
    const tableRegexes = [
      // Standard markdown table with separator
      /((?:^|\n)(?:.*?)(?:\n|$))((?:\|.*?\|\n)+)(?:\|?\s*-+.*?\n)((?:\|.*?\|.*?\n)+)/g,
      // Table without separator line
      /((?:^|\n)(?:.*?)(?:\n|$))((?:\|.*?\|\n)+)((?:\|.*?\|.*?\n)+)/g,
      // Simple table format
      /((?:\|.*?\|\n)+)((?:\|.*?\|.*?\n)+)/g
    ];
    
    for (const regex of tableRegexes) {
      let match;
      while ((match = regex.exec(markdown)) !== null) {
        tableCount++;
        let heading = '';
        let tableText = '';
        
        if (match[1]) {
          heading = match[1].trim();
          heading = heading.replace(/^#+\s*/, '').replace(/\*\*(.*?)\*\*/, '$1').replace(/\*$/, '').trim();
          tableText = (match[2] + (match[3] || '')).trim();
        } else {
          tableText = (match[1] + (match[2] || '')).trim();
        }
        
        const lines = tableText.split('\n').filter(Boolean);
        if (lines.length < 2) continue;
        
        const headers = lines[0].split('|').map(h => h.trim()).filter(Boolean);
        const rows = lines.slice(1).map(line => line.split('|').map(cell => cell.trim()).filter(Boolean));
        
        if (headers.length > 0 && rows.length > 0) {
          results.push({ heading: heading || `Table ${tableCount}`, table: { headers, rows } });
        }
      }
    }
    
    // Extract JSON tables
    const jsonBlocks = this.extractJsonFromMarkdown(markdown);
    
    for (const json of jsonBlocks) {
      try {
        const obj = JSON.parse(json);
        for (const key of Object.keys(obj)) {
          const tableObj = obj[key];
          let table: TableData = this.transformTable(tableObj);
          if (table.headers.length > 0 && table.rows.length > 0) {
            results.push({ heading: key, table });
          }
        }
      } catch (e) {
        // JSON parsing error - continue silently
      }
    }
    
    return results;
  }

  static transformSessionData(sessionData: any) {
    const output: SessionOutput = sessionData.output;
    if (!output || !output.content) throw new Error('Invalid session data structure');
    
    // Fallback-friendly approach: support legacy 'combined output' and new 'Cost Benchmarking'
    const hasCombinedOutput = Boolean(output.content['combined output']);
    const hasCostBenchmarking = Boolean(output.content['Cost Benchmarking']);

    // Determine which tab's markdown to use for extracting tables
    let markdownToParse = '';
    let activeTabName = '';
    
    if (hasCombinedOutput) {
      const mdItem = output.content['combined output'].find((i: any) => i.type === 'markdown');
      if (mdItem?.content) {
        markdownToParse = mdItem.content;
        activeTabName = 'combined output';
      }
    } else if (hasCostBenchmarking) {
      const mdItem = output.content['Cost Benchmarking'].find((i: any) => i.type === 'markdown');
      if (mdItem?.content) {
        markdownToParse = mdItem.content;
        activeTabName = 'Cost Benchmarking';
      }
    }

    // Extract all tables from markdown
    const allMarkdownTables = markdownToParse ? this.extractAllTablesFromMarkdown(markdownToParse) : [];

    // Create proper table structures
    const emptyTable: TableData = { headers: [], rows: [] };
    
    // For vendor-to-vendor comparison (tab1)
    const vendorToVendorTables: TabData = { 
      table1: allMarkdownTables.length > 0 ? allMarkdownTables[0].table : emptyTable, 
      table2: allMarkdownTables.length > 1 ? allMarkdownTables[1].table : emptyTable, 
      table3: allMarkdownTables.length > 2 ? allMarkdownTables[2].table : emptyTable 
    };
    
    // For vendor-to-benchmark comparison (tab2) - use same data for now
    const vendorToBenchmarkTables: TabData = { 
      table1: allMarkdownTables.length > 0 ? allMarkdownTables[0].table : emptyTable, 
      table2: allMarkdownTables.length > 1 ? allMarkdownTables[1].table : emptyTable, 
      table3: allMarkdownTables.length > 2 ? allMarkdownTables[2].table : emptyTable 
    };

    // Tabs: prefer what backend provides (works for new schema)
    const tabs = Array.isArray(output.tabs) && output.tabs.length > 0
      ? output.tabs
      : (hasCombinedOutput ? ['combined output'] : (hasCostBenchmarking ? ['Cost Benchmarking'] : []));

    const vendorTotalsFromSummary = this.extractVendorTotalsFromSummary(sessionData);

    return {
      tab1Data: vendorToVendorTables,
      tab2Data: vendorToBenchmarkTables,
      tabs,
      vendorTotalsFromSummary,
      allMarkdownTables
    };
  }

  private static extractJsonFromMarkdown(content: string): string[] {
    const jsonBlocks: string[] = [];
    const jsonRegex = /```json\s*\n([\s\S]*?)\n```/g;
    let match;
    while ((match = jsonRegex.exec(content)) !== null) {
      jsonBlocks.push(match[1]);
    }
    const rawJsonRegex = /({[\s\S]*?})\s*(?=\{|$)/g;
    while ((match = rawJsonRegex.exec(content)) !== null) {
      try {
        JSON.parse(match[1]);
        jsonBlocks.push(match[1]);
      } catch {}
    }
    return jsonBlocks;
  }

  private static transformTable(tableObj: any): TableData {
    if (tableObj && Array.isArray(tableObj.headers) && Array.isArray(tableObj.rows)) {
      return { headers: tableObj.headers, rows: tableObj.rows };
    }
    if (tableObj && Array.isArray(tableObj.columns) && Array.isArray(tableObj.data)) {
      return { headers: tableObj.columns, rows: tableObj.data };
    }
    return { headers: [], rows: [] };
  }

  private static validateTable(tableObj: any): TableData {
    const table = this.transformTable(tableObj);
    if (!table.headers.length || !table.rows.length) {
      throw new Error('Table data is missing or incomplete');
    }
    return table;
  }

  private static validateTableSafe(tableObj: any): TableData {
    try {
      return this.validateTable(tableObj);
    } catch {
      return { headers: [], rows: [] };
    }
  }

  static transformForCharts(tabData: TabData) {
    const { table1 } = tabData;
    if (!table1 || !table1.headers || !table1.headers.length || !table1.rows || !table1.rows.length) {
      throw new Error('Chart data table is empty');
    }

    const priceColumns = table1.headers
      .map((header, index) => ({ header, index }))
      .filter(({ header }) => header.toLowerCase().includes('price') && !header.toLowerCase().includes('market'));

    const vendorTotals = priceColumns.map(({ header, index }) => {
      const vendor = header.replace(/ Price/i, '');
      const total = table1.rows.reduce((sum, row) => {
        const price = this.extractNumber(row[index]);
        return sum + price;
      }, 0);
      return { vendor, total };
    });

    const chartData = table1.rows.map(row => {
      const item = row[0] as string;
      const data: any = { name: item };
      priceColumns.forEach(({ header, index }) => {
        const vendor = header.replace(/ Price/i, '');
        data[vendor] = this.extractNumber(row[index]);
      });
      return data;
    });

    return {
      vendorTotals,
      chartData,
      varianceData: []
    };
  }

  private static extractNumber(value: any): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const cleaned = value.replace(/[^\d.-]/g, '');
      const num = parseFloat(cleaned);
      return isNaN(num) ? 0 : num;
    }
    return 0;
  }
}

export default DataTransformer;